'use client';
import { jsx as _jsx } from "react/jsx-runtime";
/**
 * usePixelTracking Hook & Provider
 *
 * Thin React adapter over `core/pixelTracker`. The provider creates a tracker
 * for the current `stepConfig.pixels`, exposes `{ track, pixelsInitialized }`
 * via context, and disposes/recreates the tracker when pixels change.
 *
 * Init + fire logic lives in `core/pixelTracker` so non-React entries (Studio
 * islands, full-SPA runtime) can fire pixels without mounting a provider.
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import { createPixelTracker, } from '../../core/pixelTracker';
import { useStepConfig } from './useStepConfig';
const PixelTrackingContext = createContext(null);
export function PixelTrackingProvider({ children }) {
    const { pixels } = useStepConfig();
    const trackerRef = useRef(null);
    const [pixelsInitialized, setPixelsInitialized] = useState(false);
    // Recreate the tracker when `pixels` changes. The underlying init helpers
    // are idempotent — base scripts and per-pixel `init` calls de-duplicate
    // themselves so a rebuild won't double-load scripts.
    useEffect(() => {
        if (!pixels)
            return;
        const tracker = createPixelTracker(pixels);
        trackerRef.current = tracker;
        setPixelsInitialized(false);
        let cancelled = false;
        tracker.initPromise.then(() => {
            if (!cancelled)
                setPixelsInitialized(true);
        });
        return () => {
            cancelled = true;
            trackerRef.current = null;
        };
    }, [pixels]);
    const value = useMemo(() => ({
        track: (eventName, parameters, options) => {
            trackerRef.current?.track(eventName, parameters, options);
        },
        pixelsInitialized,
    }), [pixelsInitialized]);
    return _jsx(PixelTrackingContext.Provider, { value: value, children: children });
}
export function usePixelTracking() {
    const context = useContext(PixelTrackingContext);
    if (!context)
        throw new Error('usePixelTracking must be used within a PixelTrackingProvider');
    return context;
}
